import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAutomationClient } from '../providers/AutomationProvider'
import type {
  ErrorsListParams,
  ErrorStatus,
  ErrorSeverity,
  AssignErrorParams,
  BulkUpdateErrorsParams,
  CreateErrorCommentParams,
} from '../types'

/**
 * Query key factory for errors-related queries
 */
export const errorsKeys = {
  all: ['errors'] as const,
  lists: () => [...errorsKeys.all, 'list'] as const,
  list: (params?: ErrorsListParams) => [...errorsKeys.lists(), params] as const,
  details: () => [...errorsKeys.all, 'detail'] as const,
  detail: (id: string) => [...errorsKeys.details(), id] as const,
  comments: (id: string) => [...errorsKeys.detail(id), 'comments'] as const,
  search: (q: string, params?: { limit?: number; offset?: number }) =>
    [...errorsKeys.all, 'search', q, params] as const,
}

/**
 * Hook to fetch a paginated list of error groups
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useErrors({ status: 'OPEN', severity: 'CRITICAL' })
 * console.log(data?.groups)
 * ```
 */
export function useErrors(params?: ErrorsListParams) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: errorsKeys.list(params),
    queryFn: () => client.errors.list(params),
  })
}

/**
 * Hook to fetch a single error group with its occurrences
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useError('error-123')
 * console.log(data?.occurrences)
 * ```
 */
export function useError(id: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: errorsKeys.detail(id),
    queryFn: () => client.errors.get(id),
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * Hook to update an error's status
 *
 * @example
 * ```tsx
 * const { mutate: updateStatus } = useUpdateErrorStatus()
 * updateStatus({ id: 'error-123', status: 'RESOLVED' })
 * ```
 */
export function useUpdateErrorStatus() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ErrorStatus }) =>
      client.errors.updateStatus(id, status),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: errorsKeys.lists() })
      queryClient.setQueryData(errorsKeys.detail(id), (old: unknown) =>
        old ? { ...old, status: data.status } : old
      )
    },
  })
}

/**
 * Hook to update an error's severity
 *
 * @example
 * ```tsx
 * const { mutate: updateSeverity } = useUpdateErrorSeverity()
 * updateSeverity({ id: 'error-123', severity: 'CRITICAL' })
 * ```
 */
export function useUpdateErrorSeverity() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, severity }: { id: string; severity: ErrorSeverity }) =>
      client.errors.updateSeverity(id, severity),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: errorsKeys.lists() })
      queryClient.setQueryData(errorsKeys.detail(id), (old: unknown) =>
        old ? { ...old, severity: data.severity } : old
      )
    },
  })
}

/**
 * Hook to sync errors from runs
 *
 * @example
 * ```tsx
 * const { mutate: syncErrors, isPending } = useSyncErrors()
 * syncErrors()
 * ```
 */
export function useSyncErrors() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => client.errors.sync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: errorsKeys.all })
    },
  })
}

// ============ ERROR ENHANCEMENT HOOKS ============

/**
 * Hook to assign an error to a user
 */
export function useAssignError() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssignErrorParams }) =>
      client.errorEnhancements.assign(id, data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: errorsKeys.lists() })
      queryClient.setQueryData(errorsKeys.detail(id), (old: unknown) =>
        old ? { ...old, ...data } : old
      )
    },
  })
}

/**
 * Hook to bulk update errors
 */
export function useBulkUpdateErrors() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BulkUpdateErrorsParams) =>
      client.errorEnhancements.bulkUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: errorsKeys.all })
    },
  })
}

/**
 * Hook to search errors with full-text search
 */
export function useSearchErrors(q: string, params?: { limit?: number; offset?: number }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: errorsKeys.search(q, params),
    queryFn: () => client.errorEnhancements.search(q, params),
    enabled: !!q && q.length >= 2,
  })
}

/**
 * Hook to fetch error comments
 */
export function useErrorComments(id: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: errorsKeys.comments(id),
    queryFn: () => client.errorEnhancements.getComments(id),
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * Hook to add a comment to an error
 */
export function useAddErrorComment() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateErrorCommentParams }) =>
      client.errorEnhancements.addComment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: errorsKeys.comments(id) })
      queryClient.invalidateQueries({ queryKey: errorsKeys.detail(id) })
    },
  })
}
